import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';
import { ButtonComponent } from '../button/button.component';

export type DropzoneState = 'idle' | 'uploading' | 'complete';

export interface DropzoneUploadStatus {
	state: DropzoneState;
	progress: number;
	totalFiles: number;
	uploadedFiles: number;
	errorCount: number;
}

@Component({
	selector: 'app-file-dropzone',
	imports: [CommonModule, ButtonComponent, LucideAngularModule],
	templateUrl: './file-dropzone.component.html',
	styleUrls: ['./file-dropzone.component.scss'],
})
export class FileDropzoneComponent {
	// Dropzone configuration
	@Input() title: string = 'Arrastra aquí tus archivos';
	@Input() subtitle: string = 'También puedes seleccionar desde tu ordenador';
	@Input() selectText: string = 'seleccionar'; // Clickable text to select files
	@Input() buttonLabel: string = 'Subir archivos';
	@Input() accept: string = '*';
	@Input() multiple: boolean = true;
	@Input() imageSrc: string = 'assets/layout/images/document.png';
	@Input() imageAlt: string = 'Subir archivos';
	@Input() iconName: string = 'FileText';
	@Input() backgroundColor: string = 'var(--color-white)'; // Color de fondo personalizable
	/**
	 * `page` fills the viewport (standalone upload screens); `compact` shrinks to
	 * its content so it can live as one more field inside a dialog or a wizard
	 * step without forcing the panel to scroll (QA INC-04).
	 */
	@Input() size: 'page' | 'compact' = 'page';

	// Upload state inputs
	@Input() state: DropzoneState = 'idle';
	@Input() progress: number = 0;
	@Input() totalFiles: number = 0;
	@Input() uploadedFiles: number = 0;
	@Input() errorCount: number = 0;

	// Labels configurables
	@Input() uploadingText: string = 'Subiendo archivos';
	@Input() filesCountTemplate: string = ''; // Counter template, e.g. "{{uploaded}} of {{total}} files"
	@Input() completeText: string = 'Todos los archivos han sido subidos';
	@Input() viewFilesLabel: string = 'Ver archivos';
	@Input() viewErrorsLabel: string = 'Ver archivos con problemas';

	@Output() filesSelected = new EventEmitter<File[]>();
	@Output() viewFiles = new EventEmitter<void>();
	@Output() viewErrors = new EventEmitter<void>();
	@Output() reset = new EventEmitter<void>();

	isDragOver = false;

	get hasErrors(): boolean {
		return this.errorCount > 0;
	}

	get errorMessage(): string {
		return `Hemos tenido problemas con ${this.errorCount} archivo${this.errorCount > 1 ? 's' : ''}`;
	}

	get filesCountText(): string {
		if (this.filesCountTemplate) {
			return this.filesCountTemplate
				.replace('{{uploaded}}', this.uploadedFiles.toString())
				.replace('{{total}}', this.totalFiles.toString());
		}
		// Fall back to the default text
		if (this.state === 'uploading') {
			return `${this.uploadedFiles} de ${this.totalFiles} archivos`;
		}
		return `${this.totalFiles} archivo${this.totalFiles !== 1 ? 's' : ''}`;
	}

	onDragOver(event: DragEvent) {
		event.preventDefault();
		event.stopPropagation();
		if (this.state === 'idle') {
			this.isDragOver = true;
		}
	}

	onDragLeave(event: DragEvent) {
		event.preventDefault();
		event.stopPropagation();
		this.isDragOver = false;
	}

	onDrop(event: DragEvent) {
		event.preventDefault();
		event.stopPropagation();
		this.isDragOver = false;

		if (this.state !== 'idle') return;

		const files = event.dataTransfer?.files;
		if (files && files.length > 0) {
			this.handleFiles(files);
		}
	}

	onFileSelect(event: Event) {
		const input = event.target as HTMLInputElement;
		const files = input.files;
		if (files && files.length > 0) {
			this.handleFiles(files);
		}
		input.value = '';
	}

	triggerFileInput(fileInput: HTMLInputElement) {
		if (this.state === 'idle') {
			fileInput.click();
		}
	}

	onViewFiles() {
		this.viewFiles.emit();
	}

	onViewErrors() {
		this.viewErrors.emit();
	}

	onReset() {
		this.reset.emit();
	}

	private handleFiles(fileList: FileList) {
		const filesArray = Array.from(fileList);
		this.filesSelected.emit(filesArray);
	}
}
